"use strict";
import { useState, useEffect, useMemo } from "react";
import { CurrencyAmount } from "@uniswap/sdk-core";
import { useTokenAllowance, useUpdateTokenAllowance } from "hooks/useTokenAllowance";
import { useAccount } from "hooks/useAccount";
import { useTransactionAdder, useIsTransactionPending } from "state/transactions/hooks";
export function useTokenApproval(token, spender, amount, logoURI) {
  const account = useAccount();
  const addTransaction = useTransactionAdder();
  const [isApproving, setIsApproving] = useState(false);
  const [approvalTxHash, setApprovalTxHash] = useState(void 0);
  const [error, setError] = useState(null);
  const amountToApprove = useMemo(() => {
    if (!token || !amount)
      return void 0;
    try {
      return CurrencyAmount.fromRawAmount(token, amount);
    } catch {
      return void 0;
    }
  }, [token, amount]);
  const { tokenAllowance, isSyncing } = useTokenAllowance(
    token ?? void 0,
    account.address,
    // owner (user's address)
    spender ?? void 0
    // spender (router contract address)
  );
  const updateTokenAllowance = useUpdateTokenAllowance(
    amountToApprove,
    spender ?? ""
  );
  const needsApproval = useMemo(() => {
    if (!amountToApprove || !tokenAllowance || !spender)
      return false;
    return tokenAllowance.lessThan(amountToApprove);
  }, [amountToApprove, tokenAllowance, spender]);
  const approvalPending = useIsTransactionPending(approvalTxHash);
  const approve = async () => {
    if (!amountToApprove || !spender)
      return;
    setIsApproving(true);
    setError(null);
    setApprovalTxHash(void 0);
    try {
      const { response, info } = await updateTokenAllowance();
      const extendedInfo = {
        ...info,
        tokenSymbol: token?.symbol,
        tokenLogoURI: logoURI || token?.logoURI
        // Use provided logoURI or from token list
      };
      addTransaction(response, extendedInfo);
      setApprovalTxHash(response.hash);
    } catch (err) {
      console.error("Approval failed:", err);
      setError(err instanceof Error ? err.message : "Approval failed");
      setApprovalTxHash(void 0);
    } finally {
      setIsApproving(false);
    }
  };
  useEffect(() => {
    if (!needsApproval && approvalTxHash && !approvalPending) {
      setApprovalTxHash(void 0);
    }
  }, [needsApproval, approvalTxHash, approvalPending]);
  return {
    needsApproval,
    isApproving,
    approvalPending,
    approve,
    error
  };
}
